import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { inputCls, btnCls, useConfig } from "./crud";

const blank = { email: "", password: "", pin: "", name: "", role: "staff", active: true };

export default function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const config = useConfig();

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const { id, ...data } = editing;
      if (id) await api.put(`/admin/users/${id}`, data);
      else await api.post("/admin/users", data);
      toast.success("Guardado"); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar usuario?")) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="users-admin">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-xl font-light">Usuarios</h1>
        <button onClick={() => setEditing({ ...blank })} className={btnCls} data-testid="users-add-btn"><Plus size={14} /> AÑADIR</button>
      </div>
      <div className="border border-slate-200 rounded-lg bg-white shadow-sm overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="bg-slate-50">
            <tr>{["EMAIL", "NOMBRE", "ROL", "ACTIVO", ""].map((h, i) => <th key={i} className="font-mono-tech text-[10px] tracking-widest text-slate-500 px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-slate-100" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3 text-slate-600">{u.name}</td>
                <td className="px-4 py-3"><span className={`font-mono-tech text-[10px] tracking-widest ${u.role === "admin" ? "text-[#5698C7]" : "text-slate-500"}`}>{u.role.toUpperCase()}</span></td>
                <td className="px-4 py-3 text-slate-600">{u.active ? "SÍ" : "NO"}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-3 justify-end">
                    <button onClick={() => setEditing({ ...u, password: "", pin: "" })} className="text-slate-500 hover:text-[#5698C7]" data-testid={`user-edit-${u.id}`}><Pencil size={14} /></button>
                    <button onClick={() => remove(u.id)} className="text-slate-500 hover:text-red-600" data-testid={`user-delete-${u.id}`}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md p-6" data-testid="user-form-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-sm">{editing.id ? "Editar usuario" : "Nuevo usuario"}</h3>
              <button onClick={() => setEditing(null)}><X size={16} className="text-slate-500" /></button>
            </div>
            <div className="space-y-4">
              <input className={inputCls} placeholder="Email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} data-testid="user-email-input" />
              <input className={inputCls} placeholder="Nombre" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="user-name-input" />
              <input className={inputCls} type="password" placeholder={editing.id ? "Nueva contraseña (opcional)" : "Contraseña"} value={editing.password} onChange={(e) => setEditing({ ...editing, password: e.target.value })} data-testid="user-password-input" />
              <input className={inputCls} type="text" inputMode="numeric" maxLength={6} placeholder={editing.id ? "Nuevo PIN de acceso — 6 dígitos (opcional)" : "PIN de acceso (6 dígitos)"} value={editing.pin || ""} onChange={(e) => setEditing({ ...editing, pin: e.target.value.replace(/\D/g, "") })} data-testid="user-pin-input" />
              <select className={inputCls} value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} data-testid="user-role-select">
                {(config?.user_roles || []).map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <label className="flex items-center gap-2 text-xs text-slate-500">
                <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} data-testid="user-active-checkbox" /> Activo
              </label>
            </div>
            <div className="mt-6 flex justify-end"><button onClick={save} className={btnCls} data-testid="user-save-btn">GUARDAR</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
